import { getChampionById } from "constants/gameDefaults";
import drafterLogo from "styles/images/drafterWhiteLogo.png";
import { prefetchImages, fetchImageAsBase64 } from "utils/imageUtils";

// ─── Card dimensions ───
const CARD_W = 1200;
const CARD_H = 675;
const SCALE = 2; // retina

// Layout
const PAD_X = 40;
const HEADER_H = 80;
const BAND_H = 255;
const SPLASH_W = 200;
const SPLASH_H = 170;
const SPLASH_GAP = 14;
const VS_H = 30;

// Colors
const COL = {
  bg: "#1a1b1f",
  white: "#ffffff",
  muted: "rgba(255,255,255,0.5)",
  green: "#81db6b",
  vs: "rgba(255,255,255,0.3)",
  splashBg: "#2a2b30",
  splashBorder: "rgba(255,255,255,0.15)",
};

const PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPj/HwADBwIAMCbHYQAAAABJRU5ErkJggg==";

// ─── Helpers ───

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // eslint-disable-next-line fp/no-mutation
    img.onload = () => resolve(img);
    // eslint-disable-next-line fp/no-mutation
    img.onerror = () => reject(new Error("Image load failed"));
    // eslint-disable-next-line fp/no-mutation
    img.src = src;
  });
}

function drawImageCover(ctx, img, x, y, w, h) {
  const imgRatio = img.width / img.height;
  const slotRatio = w / h;
  // eslint-disable-next-line fp/no-let
  let sx;
  // eslint-disable-next-line fp/no-let
  let sy;
  // eslint-disable-next-line fp/no-let
  let sw;
  // eslint-disable-next-line fp/no-let
  let sh;
  if (imgRatio > slotRatio) {
    // eslint-disable-next-line fp/no-mutation
    sh = img.height;
    // eslint-disable-next-line fp/no-mutation
    sw = sh * slotRatio;
    // eslint-disable-next-line fp/no-mutation
    sx = (img.width - sw) / 2;
    // eslint-disable-next-line fp/no-mutation
    sy = 0;
  } else {
    // eslint-disable-next-line fp/no-mutation
    sw = img.width;
    // eslint-disable-next-line fp/no-mutation
    sh = sw / slotRatio;
    // eslint-disable-next-line fp/no-mutation
    sx = 0;
    // eslint-disable-next-line fp/no-mutation
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

async function drawTeamBand(ctx, team, bandY, gradColors) {
  const bandW = CARD_W - 2 * PAD_X;

  ctx.save();
  roundRect(ctx, PAD_X, bandY, bandW, BAND_H, 12);
  ctx.clip();

  // Gradient background
  const grad = ctx.createLinearGradient(
    PAD_X,
    bandY,
    PAD_X + bandW,
    bandY + BAND_H,
  );
  grad.addColorStop(0, gradColors[0]);
  grad.addColorStop(0.5, gradColors[1]);
  grad.addColorStop(1, gradColors[2]);
  ctx.fillStyle = grad;
  ctx.fillRect(PAD_X, bandY, bandW, BAND_H);

  // Username
  // eslint-disable-next-line fp/no-mutation
  ctx.font = "700 24px Montserrat, 'Work Sans', sans-serif";
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.white;
  // eslint-disable-next-line fp/no-mutation
  ctx.textBaseline = "top";
  // eslint-disable-next-line fp/no-mutation
  ctx.textAlign = "left";
  ctx.fillText(team.username.toUpperCase(), PAD_X + 28, bandY + 14);

  // Splash arts
  const totalSplashW = 5 * SPLASH_W + 4 * SPLASH_GAP;
  const splashStartX = (CARD_W - totalSplashW) / 2;
  const splashY = bandY + 48;

  for (let i = 0; i < team.champions.length; i += 1) {
    const x = splashStartX + i * (SPLASH_W + SPLASH_GAP);
    const champ = team.champions[i];

    // Rounded splash slot
    ctx.save();
    roundRect(ctx, x, splashY, SPLASH_W, SPLASH_H, 8);
    ctx.clip();

    // Background
    // eslint-disable-next-line fp/no-mutation
    ctx.fillStyle = COL.splashBg;
    ctx.fillRect(x, splashY, SPLASH_W, SPLASH_H);

    // Splash image — only draw if it looks like a real image (>5px)
    try {
      // eslint-disable-next-line no-await-in-loop
      const img = await loadImage(champ.splashBase64);
      if (img.naturalWidth > 5 && img.naturalHeight > 5) {
        drawImageCover(ctx, img, x, splashY, SPLASH_W, SPLASH_H);
      }
    } catch {
      // Keep grey background
    }

    ctx.restore();

    // Border (outside clip)
    ctx.save();
    roundRect(ctx, x, splashY, SPLASH_W, SPLASH_H, 8);
    // eslint-disable-next-line fp/no-mutation
    ctx.strokeStyle = COL.splashBorder;
    // eslint-disable-next-line fp/no-mutation
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore(); // band clip
}

// ─── Public API ───

/**
 * Builds the data object needed for card generation.
 * Maps user/opponent → blue/red, prefetches images as base64.
 */
export async function buildCardData({
  userCharacters,
  opponentCharacters,
  userUsername,
  opponentUsername,
  percentages,
  isUserBlueSide,
}) {
  const blueChampionIds = isUserBlueSide ? userCharacters : opponentCharacters;
  const redChampionIds = isUserBlueSide ? opponentCharacters : userCharacters;
  const blueUsername = isUserBlueSide ? userUsername : opponentUsername;
  const redUsername = isUserBlueSide ? opponentUsername : userUsername;
  const bluePercentage = isUserBlueSide ? percentages[0] : percentages[1];
  const redPercentage = isUserBlueSide ? percentages[1] : percentages[0];

  const blueChampions = blueChampionIds.map((id) => {
    const champ = getChampionById(id);
    return { id, name: champ?.name || id, splashUrl: champ?.splash };
  });
  const redChampions = redChampionIds.map((id) => {
    const champ = getChampionById(id);
    return { id, name: champ?.name || id, splashUrl: champ?.splash };
  });

  const allSplashUrls = [
    ...blueChampions.map((c) => c.splashUrl),
    ...redChampions.map((c) => c.splashUrl),
  ].filter(Boolean);

  const imageMap = await prefetchImages(allSplashUrls);

  // Convert logo to base64
  // eslint-disable-next-line fp/no-let
  let logoBase64 = PLACEHOLDER;
  try {
    logoBase64 = await fetchImageAsBase64(drafterLogo);
  } catch {
    // keep placeholder
  }

  return {
    blueTeam: {
      username: blueUsername,
      percentage: bluePercentage,
      champions: blueChampions.map((c) => ({
        name: c.name,
        splashBase64: imageMap.get(c.splashUrl) || PLACEHOLDER,
      })),
    },
    redTeam: {
      username: redUsername,
      percentage: redPercentage,
      champions: redChampions.map((c) => ({
        name: c.name,
        splashBase64: imageMap.get(c.splashUrl) || PLACEHOLDER,
      })),
    },
    logoBase64,
  };
}

/**
 * Draws the draft card on a Canvas and returns a PNG data URL.
 * No DOM rendering, no html-to-image — pure Canvas API.
 */
export async function generateCardImage({ blueTeam, redTeam, logoBase64 }) {
  // Wait for web fonts (Montserrat) to be available
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }

  const canvas = document.createElement("canvas");
  // eslint-disable-next-line fp/no-mutation
  canvas.width = CARD_W * SCALE;
  // eslint-disable-next-line fp/no-mutation
  canvas.height = CARD_H * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);

  // ─── Background ───
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // ─── Header ───
  const blueIsWinning = blueTeam.percentage >= redTeam.percentage;
  const winningSide = blueIsWinning ? "Blue" : "Red";
  const winPct = blueIsWinning ? blueTeam.percentage : redTeam.percentage;

  const headerCY = 8 + HEADER_H / 2;
  // eslint-disable-next-line fp/no-mutation
  ctx.font = "800 32px Montserrat, 'Work Sans', sans-serif";
  const sideText = `${winningSide} side wins, `;
  const pctText = `${winPct}%`;
  const sideTextW = ctx.measureText(sideText).width;
  const pctTextW = ctx.measureText(pctText).width;
  const logoSize = 72;
  const logoGap = 20;
  const totalHeaderW = logoSize + logoGap + sideTextW + pctTextW;
  const headerStartX = (CARD_W - totalHeaderW) / 2;

  // Logo
  try {
    const logoImg = await loadImage(logoBase64);
    ctx.drawImage(
      logoImg,
      headerStartX,
      headerCY - logoSize / 2,
      logoSize,
      logoSize,
    );
  } catch {
    // skip logo
  }

  // "XXX side wins, "
  // eslint-disable-next-line fp/no-mutation
  ctx.textBaseline = "middle";
  // eslint-disable-next-line fp/no-mutation
  ctx.textAlign = "left";
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.white;
  ctx.fillText(sideText, headerStartX + logoSize + logoGap, headerCY);

  // "YY%" in green
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.green;
  ctx.fillText(
    pctText,
    headerStartX + logoSize + logoGap + sideTextW,
    headerCY,
  );

  // ─── Blue Band ───
  const blueBandY = HEADER_H + 6;
  await drawTeamBand(ctx, blueTeam, blueBandY, [
    "#0a1628",
    "#122a5c",
    "#1a3a7a",
  ]);

  // ─── VS Divider ───
  const vsY = blueBandY + BAND_H;
  const vsCY = vsY + VS_H / 2;
  const lineInset = PAD_X + 40;

  // Left line
  const lg1 = ctx.createLinearGradient(lineInset, vsCY, CARD_W / 2 - 30, vsCY);
  lg1.addColorStop(0, "transparent");
  lg1.addColorStop(1, COL.vs);
  // eslint-disable-next-line fp/no-mutation
  ctx.strokeStyle = lg1;
  // eslint-disable-next-line fp/no-mutation
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(lineInset, vsCY);
  ctx.lineTo(CARD_W / 2 - 30, vsCY);
  ctx.stroke();

  // "VS"
  // eslint-disable-next-line fp/no-mutation
  ctx.font = "900 16px 'Work Sans', sans-serif";
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.vs;
  // eslint-disable-next-line fp/no-mutation
  ctx.textAlign = "center";
  // eslint-disable-next-line fp/no-mutation
  ctx.textBaseline = "middle";
  ctx.fillText("VS", CARD_W / 2, vsCY);

  // Right line
  const lg2 = ctx.createLinearGradient(
    CARD_W / 2 + 30,
    vsCY,
    CARD_W - lineInset,
    vsCY,
  );
  lg2.addColorStop(0, COL.vs);
  lg2.addColorStop(1, "transparent");
  // eslint-disable-next-line fp/no-mutation
  ctx.strokeStyle = lg2;
  ctx.beginPath();
  ctx.moveTo(CARD_W / 2 + 30, vsCY);
  ctx.lineTo(CARD_W - lineInset, vsCY);
  ctx.stroke();

  // ─── Red Band ───
  const redBandY = vsY + VS_H;
  await drawTeamBand(ctx, redTeam, redBandY, ["#2a0a14", "#5c1222", "#7a1a2a"]);

  // ─── Footer ───
  // eslint-disable-next-line fp/no-mutation
  ctx.font = "500 14px 'Work Sans', sans-serif";
  // eslint-disable-next-line fp/no-mutation
  ctx.fillStyle = COL.muted;
  // eslint-disable-next-line fp/no-mutation
  ctx.textAlign = "center";
  // eslint-disable-next-line fp/no-mutation
  ctx.textBaseline = "top";
  ctx.fillText("www.Drafter.one", CARD_W / 2, redBandY + BAND_H + 16);

  return canvas.toDataURL("image/png");
}
